/**
 * PD-17093 — Jobs API shapes, shared by crm-api and the jobs module (sales
 * relationships plan, section 4). A job is the won work delivered to one
 * customer; its orders and quotes link through `order.fk_job__id` and its
 * invoices count through their lines' orders.
 *
 * Request bodies are plain types validated in the service, as the competitor
 * DTOs are, so the global whitelisting ValidationPipe never strips fields.
 */
import type { ClientEntity } from '../entity/client.entity';
import type { JobEntity, JobStatus } from '../entity/job.entity';
import type { JobCustomerEntity } from '../entity/job-customer.entity';
import type { OpportunityStageOutcome } from '../entity/m-sm-client-opportunity-stage.entity';
import type { JobSuggestionSource } from '../entity/job-suggestion-dismissal.entity';

/** Labels for the job statuses, in their display order. */
export const JOB_STATUS_LABELS: Readonly<Record<JobStatus, string>> = {
	planned: 'Planned',
	active: 'Active',
	on_hold: 'On Hold',
	closed: 'Closed',
};

/** The fields a rep edits on a job. */
export type JobEditableFieldsDto = Pick<
	JobEntity,
	| 'name'
	| 'jobNumber'
	| 'status'
	| 'description'
	| 'fkClientId'
	| 'fkUserIdSalesperson'
	| 'startDate'
	| 'endDate'
	| 'fkOpportunityId'
	| 'fkPriceContractId'
	| 'fkJobIdParent'
> & {
	/** Decimal returned as a number. PD-17830: what was entered, else what the opportunities are worth. */
	estimatedValue: number | null;
};

export interface JobTotalsDto {
	quotedTotal: number;
	orderedTotal: number;
	invoicedTotal: number;
	quoteCount: number;
	orderCount: number;
	invoiceCount: number;
	lastOrderDate: string | null;
	totalsRefreshedDate: string | null;
	/**
	 * PD-17135 — gross profit to date over the lines whose cost is known, and
	 * its margin (0–1). Sent only to users with `orders:gp:view` (the service
	 * leaves them out and the GP response filter strips them besides); null
	 * when no line has a known cost.
	 */
	orderedGp?: number | null;
	orderedGpMargin?: number | null;
	invoicedGp?: number | null;
	invoicedGpMargin?: number | null;
}

/**
 * PD-17447 — a job's figures with all its sub-jobs: the job and every job
 * beneath it. Each invoice counts once, even when its lines touch two of the
 * jobs. For a job without sub-jobs they equal the job's own.
 */
export interface JobRollupTotalsDto extends Omit<JobTotalsDto, 'totalsRefreshedDate'> {
	/** Every job's own estimate added up; null when none of them has one. */
	estimatedValue: number | null;
	/** Ordered as a share of the estimate (0–1), both with sub-jobs (decision 3); null without an estimate. */
	percentOfEstimate: number | null;
}

/** One row of the Jobs list and a customer's Jobs tab. */
export interface JobListRowDto
	extends JobEditableFieldsDto,
		JobTotalsDto,
		Pick<JobEntity, 'id'> {
	clientName: string;
	salespersonName: string | null;
	opportunityName: string | null;
	closedDate: string | null;
	createdDate: string;
	/** Ordered to date as a share of the estimated value (0–1); null without an estimate. */
	percentOfEstimate: number | null;
	/** PD-17830 — the estimate someone entered, for the job's form; null when it follows the opportunities. */
	enteredEstimatedValue: number | null;
	/** PD-17830 — what the job's own opportunities are worth; null for a reader limited to some customers. */
	opportunityEstimate: number | null;
	/** PD-17447 — the stored totals with all the job's sub-jobs (the job's own on the fields above). */
	withSubJobs: JobRollupTotalsDto;
	/** PD-17448 — how many customers the job has (the main customer, `clientName`, among them). */
	customerCount: number;
	/**
	 * PD-17449 — how many different customers the job and every job beneath
	 * it have together (the Sub-jobs tab's "4 customers"); equals
	 * `customerCount` for a job without sub-jobs.
	 */
	customerCountWithSubJobs: number;
	/** PD-17447 — every job beneath this one, at any level (0 without sub-jobs). */
	allSubJobCount: number;
	/**
	 * PD-17450 — the names of the jobs above this one, top-level job first
	 * (empty for a top-level job), so a list can show "Riverside Medical
	 * Center Expansion › Tower A" under a job named "Mechanical". Sent by the
	 * Jobs list (GET /api/jobs); the job page has `path` instead.
	 */
	pathNames?: Array<string>;
	/**
	 * PD-17791 — tree view only: this top-level job does not match the tab or
	 * filter itself and is on the list for the matching jobs beneath it.
	 */
	shownForSubJobs?: boolean;
	/**
	 * PD-17448 — true when the reader cannot open every customer on the job
	 * (or, for `withSubJobs`, on the job and its sub-jobs): the totals on the
	 * row then count only the orders of the customers they can open,
	 * computed when read (owner decision 2).
	 */
	totalsLimited: boolean;
	/**
	 * PD-17448 — on a list filtered to one customer (the customer's Jobs tab):
	 * that customer's role and status on the job.
	 */
	customerRoleName?: string | null;
	customerStatusName?: string | null;
	customerStatusOutcome?: string | null;
}

/** PD-17448 — a role or status (catalog value) as a job customer row shows it. */
export interface JobCustomerValueDto {
	id: number;
	name: string;
	/** A status's fixed outcome (JOB_CUSTOMER_STATUS_OUTCOMES); null for a role. */
	outcome: string | null;
}

/**
 * PD-17448 — one customer on a job, for the "Customers on this job" card.
 * Every customer is listed, so the reader knows who else is on the job; a
 * customer the reader cannot open shows its name, role and status only.
 */
export interface JobCustomerDto extends Pick<JobCustomerEntity, 'id' | 'fkClientId'> {
	clientName: string;
	clientExternalId: string | null;
	/** The job's main customer (`job.fk_client__id`), the one it was created for. */
	isMain: boolean;
	/** Whether the reader may open the customer (link it, see its sales, change or remove it). */
	canOpen: boolean;
	role: JobCustomerValueDto | null;
	status: JobCustomerValueDto | null;
	/** Placed orders on this job (not its sub-jobs) from this customer; null when the reader cannot open it. */
	orderedTotal: number | null;
	/** Orders and quotes on this job from this customer; null when the reader cannot open it. */
	linkedOrderCount: number | null;
	/** The customer's salesperson. */
	salespersonName: string | null;
}

/** POST /api/jobs/:id/customers — a customer the caller can open; role and status default from the lists. */
export interface AddJobCustomerRequestDto {
	clientId: number;
	roleId?: number | null;
	statusId?: number | null;
}

/** PUT /api/jobs/:id/customers/:jobCustomerId — only the fields sent change. */
export interface UpdateJobCustomerRequestDto {
	roleId?: number | null;
	statusId?: number | null;
}

/**
 * PD-17448 (owner, 10/25/26) — a customer the job's price contract covers
 * that is not on the job yet, offered on the customers card with one click to
 * add. Only customers the reader may open are offered; nothing is added on
 * its own, because a job's customers decide who may open the job.
 */
export interface JobContractCustomerSuggestionDto {
	clientId: ClientEntity['id'];
	clientName: string;
}

/** What the job customer routes return. */
export interface JobCustomersResultDto {
	customers: Array<JobCustomerDto>;
	/** The contract's other customers (see JobContractCustomerSuggestionDto), by name. */
	contractSuggestions: Array<JobContractCustomerSuggestionDto>;
	/** The job's main customer after the change. */
	fkClientId: number;
	/** On an add: the new customer's orders and quotes that linked to the job on their own (job number or contract). */
	autoLinkedOrderIds?: Array<number>;
	/** On a remove: the removed customer's orders and quotes taken off the job. */
	unlinkedOrderIds?: Array<number>;
}

/** PD-17447 — a job named in a path or as a parent: enough to show and link it. */
export interface JobTreeNodeDto extends Pick<JobEntity, 'id' | 'name'> {
	/** Whether the caller may open the job (show it as a link, or as plain text). */
	canOpen: boolean;
}

/**
 * PD-17447 — the opportunities on a job, by outcome. A parent's pipeline adds
 * up its sub-jobs' opportunities (decision 9); each opportunity is on one job,
 * so it counts once.
 */
export interface JobPipelineDto {
	openCount: number;
	openAmount: number;
	wonCount: number;
	wonAmount: number;
	/** PD-17795 — opportunities left out because a competing one already counts. */
	foldedCount: number;
}

export interface JobShipToDto {
	id: number;
	fkClientAddressId: number;
	name: string | null;
	address: string;
}

export interface JobContactDto {
	id: number;
	fkContactId: number;
	name: string;
	title: string | null;
	email: string | null;
	phone: string | null;
	role: string | null;
}

/** PUT /api/jobs/:id/competing-opportunities — mark opportunities on the job as competing, or take them out. */
export interface SetCompetingOpportunitiesRequestDto {
	opportunityIds: Array<number>;
	competing: boolean;
}

/**
 * PD-17109 — an opportunity on a job (`m_sm_client_opportunity.fk_job__id`):
 * a piece of the job's work being bid or won. A job can hold many.
 */
export interface JobOpportunityDto {
	id: number;
	name: string;
	/** PD-17793 — the customer it is for; a job can have several. */
	clientName: string | null;
	/** PD-17795 — shared by the opportunities that compete with this one; null when it competes with none. */
	competingGroupId: number | null;
	stageName: string | null;
	/** From the stage: open, won or lost. */
	outcome: OpportunityStageOutcome;
	amount: number | null;
	closeDate: string | null;
	salespersonName: string | null;
	/** The opportunity the job was created from (`job.fk_m_sm_client_opportunity__id`). */
	isCreatedFrom: boolean;
}

/** PD-17814 — another opportunity on the same job, and whether it competes with this one. */
export interface OpportunityCompetingRowDto extends JobOpportunityDto {
	competing: boolean;
}

/** GET /api/opportunities/:id/competing */
export interface OpportunityCompetingDto {
	jobId: number | null;
	/** The others on the job the reader may open. */
	opportunities: Array<OpportunityCompetingRowDto>;
}

/** A job's page: the row plus its ship-tos, people and links. */
export interface JobDto extends JobListRowDto {
	priceContractName: string | null;
	shipTos: Array<JobShipToDto>;
	contacts: Array<JobContactDto>;
	/** PD-17109 — the opportunities on the job, newest first. */
	opportunities: Array<JobOpportunityDto>;
	/** PD-17448 — the customers on the job, the main customer first. */
	customers: Array<JobCustomerDto>;
	/** PD-17448 — the price contract's customers not on the job yet, to suggest. */
	contractSuggestions: Array<JobContractCustomerSuggestionDto>;
	createdByName: string | null;
	/**
	 * PD-17134 — on a create or update response only: the customer's earlier
	 * orders and quotes this save linked because their job number matched the
	 * job's number or name, or (PD-17316) because they were placed under the
	 * job's pricing contract. The web app offers Undo for them.
	 */
	autoLinkedOrderIds?: Array<number>;
	/** PD-17447 — the parent job, or null for a top-level job. */
	parentJob: JobTreeNodeDto | null;
	/** PD-17447 — the jobs above this one, the top-level job first; empty for a top-level job. */
	path: Array<JobTreeNodeDto>;
	/** PD-17447 — the job's level in its tree: 1 for a top-level job, at most JOB_TREE_MAX_DEPTH. */
	level: number;
	/** PD-17447 — the job's direct sub-jobs. */
	subJobCount: number;
	/** PD-17447 — the jobs beneath this one, at any level, that are not closed (the Close prompt offers to close them). */
	openSubJobCount: number;
	/** PD-17447 — the job's opportunities, for the job alone and with its sub-jobs. */
	pipeline: { thisJob: JobPipelineDto; withSubJobs: JobPipelineDto };
	/** PD-17795 — quote dollars counted twice by competing opportunities, taken off the job page's Quoted. */
	/**
	 * PD-17449 — the parent job's totals with all its sub-jobs (this job among
	 * them), for the "Where this job sits" card and the estimate's share of the
	 * parent. Null for a top-level job, or a parent the caller cannot open.
	 */
	parentWithSubJobs: JobRollupTotalsDto | null;
	/** PD-17447 — on an update response only: the sub-jobs closed with the job. */
	closedSubJobIds?: Array<number>;
}

/** POST /api/jobs */
export type CreateJobRequestDto = Pick<JobEditableFieldsDto, 'name' | 'fkClientId'> &
	Partial<Omit<JobEditableFieldsDto, 'name' | 'fkClientId'>> & {
		/** Orders or quotes to link at once (a won opportunity's quote, for example). */
		orderIds?: Array<number>;
	};

/**
 * PUT /api/jobs/:id — only the fields sent change. `fkJobIdParent` moves the
 * job (the same rules as PUT /api/jobs/:id/parent).
 */
export type UpdateJobRequestDto = Partial<Omit<JobEditableFieldsDto, 'fkClientId'>> & {
	/**
	 * PD-17447 — with `status: 'closed'`: also close every sub-job beneath the
	 * job that is not closed yet (decision 7). Left out or false, they stay open.
	 */
	closeOpenSubJobs?: boolean;
};

/** PUT /api/jobs/:id/parent — put the job under another job, or (null) at the top. */
export type MoveJobRequestDto = Pick<JobEditableFieldsDto, 'fkJobIdParent'>;

/**
 * PD-17447 — what deleting a job does with its sub-jobs (decision 6):
 * `move_up` (the default) puts its direct sub-jobs under its parent, or at
 * the top; `delete` deletes every job beneath it too.
 */
export const JOB_SUB_JOB_DELETE_CHOICES = ['move_up', 'delete'] as const;

export type JobSubJobDeleteChoice = (typeof JOB_SUB_JOB_DELETE_CHOICES)[number];

/** DELETE /api/jobs/:id?subJobs=move_up|delete */
export interface DeleteJobResultDto {
	/** The job and, with `delete`, every job beneath it. */
	deletedJobIds: Array<number>;
	/** With `move_up`: the direct sub-jobs that moved up. */
	movedJobIds: Array<number>;
}

/**
 * PD-17447 — one job beneath another (GET /api/jobs/:id/sub-jobs), with its
 * own totals and its totals with its sub-jobs. Rows come parent first, each
 * job's sub-jobs after it, by name.
 */
export interface JobSubTreeRowDto extends JobListRowDto {
	/** 1 for a direct sub-job of the job asked about, 2 for its sub-jobs, and so on. */
	depth: number;
	/** The row's direct sub-jobs. */
	subJobCount: number;
}

export interface JobSubTreeResponseDto {
	success: true;
	data: Array<JobSubTreeRowDto>;
}

/** An order or quote on a job's page. */
export interface JobOrderRowDto {
	id: number;
	externalId: string | null;
	isQuote: boolean;
	status: string | null;
	statusLabel: string;
	date: string | null;
	total: number;
	poNumber: string | null;
	jobNumber: string | null;
	shipToName: string | null;
	/** PD-17330 — the order's delivery date and time window (orders only). */
	deliveryDate: string | null;
	deliveryWindowStart: string | null;
	deliveryWindowEnd: string | null;
}

/** PD-17330 — an order due to be delivered on a job, for its Upcoming Deliveries list. */
export interface JobDeliveryRowDto {
	orderId: number;
	externalId: string | null;
	deliveryDate: string;
	deliveryWindowStart: string | null;
	deliveryWindowEnd: string | null;
	shipToName: string | null;
	/** The ship-to is one of the job's sites. */
	onJobSite: boolean;
	/** What is coming: the first lines, "8" C900 pipe ×600, gate valve ×6", and how many more. */
	contents: string;
	status: string | null;
	statusLabel: string;
	total: number;
}

/** GET /api/jobs/:id/deliveries */
export interface JobDeliveriesDto {
	upcoming: Array<JobDeliveryRowDto>;
	/** Open orders on the job with no delivery date yet. */
	undatedCount: number;
}

/** An invoice on a job's page (through its lines' orders). */
export interface JobInvoiceRowDto {
	id: number;
	externalId: string | null;
	date: string | null;
	dueDate: string | null;
	total: number;
	balance: number | null;
	status: string | null;
}

/** A job in a picker (orders and quotes, a won opportunity). */
export interface JobOptionDto {
	id: number;
	name: string;
	jobNumber: string | null;
	status: JobStatus;
	fkClientId: number;
	clientName: string;
	/** PD-17450 — the names of the jobs above it, top-level job first; empty for a top-level job. */
	pathNames: Array<string>;
}

/** A note on a job (a client note carrying the job's id). */
export interface JobNoteDto {
	id: number;
	note: string;
	dateTime: string;
	createdByName: string | null;
}

export interface JobListResponseDto {
	success: true;
	data: Array<JobListRowDto>;
	/**
	 * PD-17450 — `tabCount`, in the tree view only: every job matching the
	 * tab, for its badge, while `total` counts the top-level jobs being paged.
	 */
	pagination: { page: number; limit: number; total: number; totalPages: number; tabCount?: number };
}

export interface JobPagedResponseDto<T> {
	success: true;
	data: Array<T>;
	pagination: { page: number; limit: number; total: number; totalPages: number };
}

/**
 * PD-17095 — one "Possible jobs" suggestion (JQ-4). `key` identifies it within
 * its source (the lower-cased job name, the ship-to's address id or the price
 * contract id); `clientId` is null for a contract, whose customer the rep picks.
 */
export interface JobSuggestionDto {
	source: JobSuggestionSource;
	key: string;
	clientId: number | null;
	clientName: string | null;
	/** The suggested job name. */
	title: string;
	/** Where it comes from, in words ("Ship-to 123 Main St, Detroit"). */
	detail: string | null;
	orderCount: number;
	total: number;
	lastOrderDate: string | null;
	clientAddressId: number | null;
	priceContractId: number | null;
}

/** POST /api/jobs/suggestions/dismiss */
export interface DismissJobSuggestionRequestDto {
	source: JobSuggestionSource;
	key: string;
	clientId: number | null;
}

/** POST /api/jobs/:id/apply-suggestion — link what the suggestion names to the job. */
export interface ApplyJobSuggestionRequestDto {
	source: JobSuggestionSource;
	key: string;
	/**
	 * PD-17448 — for a job-name suggestion, the customer it is about: only
	 * that customer's orders link, and it must be on the job. Left out, the
	 * orders of every customer on the job the caller may open link.
	 */
	clientId?: number | null;
}

/** PD-17109 — the job an opportunity is on, as the opportunity page and the Sales Helper show it. */
export interface OpportunityJobSummaryDto {
	id: number;
	name: string;
	jobNumber: string | null;
	status: JobStatus;
	/**
	 * PD-17452 — the names of the jobs above it, top-level job first (empty
	 * for a top-level job), so the opportunity says which "Mechanical" it is on.
	 */
	pathNames: Array<string>;
}

/** GET /api/opportunities/:id/job */
export interface OpportunityJobDto {
	opportunityId: number;
	opportunityName: string;
	clientId: number;
	amount: number | null;
	/** The opportunity's linked quote or order. */
	orderId: number | null;
	job: OpportunityJobSummaryDto | null;
	/**
	 * On a link response only: whether the opportunity's quote or order went
	 * on the job too (not when it is on another job, or the caller cannot edit jobs).
	 */
	orderOnJob?: boolean;
}

/** PUT /api/opportunities/:id/job */
export interface LinkOpportunityJobRequestDto {
	jobId: number;
	/** Move it from the job it is on now; without it that is refused (409). */
	move?: boolean;
}

/** GET /api/jobs/for-order/:orderId */
export interface OrderJobDto {
	orderId: number;
	clientId: number;
	job: JobOptionDto | null;
}
